/**
 * Public pages and their search metadata. One source for:
 *   - scripts/prerender.mjs (static HTML per page, sitemap.xml)
 *   - <RouteMeta /> (title / description / canonical while browsing)
 *
 * Copy is Turkish: the site sells in Turkey and renders Turkish by default.
 * Keep titles under ~60 characters and descriptions under ~155.
 */

export const SITE_URL = "https://meethumo.com";
export const SITE_NAME = "Hümo";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

export interface PublicRoute {
  path: string;
  title: string;
  description: string;
  /** false: rendered for visitors but kept out of the index and the sitemap. */
  index: boolean;
  /** Sitemap hints for indexable pages. */
  priority?: number;
  changefreq?: "daily" | "weekly" | "monthly" | "yearly";
  /** YYYY-MM-DD of the last real content change; goes into sitemap.xml. */
  lastmod?: string;
}

export const PUBLIC_ROUTES: PublicRoute[] = [
  {
    path: "/",
    title: "Hümo — WhatsApp, Instagram ve web için AI satış asistanı",
    description:
      "Hümo müşterilerinize işletmenizin bilgisiyle anında cevap verir: fiyat ve stoğu söyler, lead'i kaydeder, randevu kurar. Ücretsiz plan, kredi kartı gerekmez.",
    index: true,
    priority: 1,
    changefreq: "weekly"
  },
  {
    path: "/pricing",
    title: "Fiyatlar — Hümo AI Satış Asistanı",
    description:
      "Ücretsiz planla başlayın, büyüdükçe yükseltin. Başlangıç, Büyüme ve Profesyonel paketleri; taahhüt yok, Ücretsiz plan için kredi kartı gerekmez.",
    index: true,
    priority: 0.9,
    changefreq: "monthly"
  },
  {
    path: "/features",
    title: "Özellikler — Tek gelen kutusu, AI cevap ve satış zekâsı | Hümo",
    description:
      "WhatsApp, Instagram, web sitesi ve Telegram tek ekranda. Kendi bilginizle cevap veren AI, lead skoru, AI koç, onaylı aksiyonlar ve ekibe devir.",
    index: true,
    priority: 0.8,
    changefreq: "monthly"
  },
  {
    path: "/randevu",
    title: "AI randevu asistanı: klinik, kuaför ve servis | Hümo",
    description:
      "Instagram, WhatsApp ve sitenizden gelen randevu taleplerini AI 7/24 karşılasın: doğru hizmet, gerçek boş saat, çift rezervasyon yok.",
    index: true,
    priority: 0.8,
    changefreq: "monthly",
    lastmod: "2026-10-21"
  },
  {
    path: "/sectors/kuafor",
    title: "Kuaför randevu programı ve Instagram DM asistanı | Hümo",
    description:
      "Kuaför ve güzellik salonları için AI: Instagram DM'lerine fiyatınızla cevap verir, işleme uyan boş saati önerir, randevuyu yazar. Çift rezervasyon yok.",
    index: true,
    priority: 0.8,
    changefreq: "monthly",
    lastmod: "2026-10-21"
  },
  {
    path: "/sectors/marina",
    title: "Marinalar ve tekne işletmeleri için AI asistan | Hümo",
    description:
      "Sezon yoğunluğunda bağlama, fiyat ve rezervasyon sorularını Hümo cevaplasın. WhatsApp ve web sitenizde 7/24, ekibinize özetle devreder.",
    index: true,
    priority: 0.7,
    changefreq: "monthly"
  },
  {
    path: "/guvenlik",
    title: "Güvenlik ve KVKK: veriniz nerede | Hümo",
    description:
      "Konuşmalar nerede saklanır, yapay zekâya ne gider, nasıl silinir. Verinin kurumunuzdan çıkmaması için Hümo'yu kendi sunucunuza kurun.",
    index: true,
    priority: 0.6,
    changefreq: "monthly",
    lastmod: "2026-10-28"
  },
  {
    path: "/about",
    title: "Hakkımızda — Hümo",
    description:
      "Hümo, işletmelerin müşteri mesajlarına anında ve doğru cevap vermesi için TUBU ARGE A.Ş. tarafından geliştirilen AI satış asistanıdır.",
    index: true,
    priority: 0.5,
    changefreq: "yearly"
  },
  {
    path: "/help-center",
    title: "Yardım Merkezi — Hümo",
    description:
      "Hümo kurulumu, kanalları bağlama, bilgi bankası ve otomatik cevap ayarları hakkında rehberler ve sık sorulan sorular.",
    index: true,
    priority: 0.5,
    changefreq: "monthly"
  },
  {
    path: "/privacy-policy",
    title: "Gizlilik Politikası ve KVKK — Hümo",
    description: "Hümo'nun kişisel verileri nasıl işlediği, sakladığı ve KVKK kapsamındaki haklarınız.",
    index: true,
    priority: 0.3,
    changefreq: "yearly"
  },
  {
    path: "/terms-of-service",
    title: "Kullanım Şartları — Hümo",
    description: "Hümo hizmetinin kullanım şartları, abonelik ve ödeme koşulları.",
    index: true,
    priority: 0.3,
    changefreq: "yearly"
  },
  {
    path: "/distance-sales-agreement",
    title: "Mesafeli Hizmet Sözleşmesi — Hümo",
    description: "Hümo ücretli planlarının satış koşulları: bedel, ödeme, ifa, cayma hakkı ve iade.",
    index: true,
    priority: 0.2,
    changefreq: "yearly"
  },
  {
    path: "/pre-information-form",
    title: "Ön Bilgilendirme Formu — Hümo",
    description: "Ödeme öncesi bilgiler: satıcı bilgileri, KDV dahil fiyatlar, ödeme şekli ve cayma hakkı.",
    index: true,
    priority: 0.2,
    changefreq: "yearly"
  },
  {
    path: "/refund-policy",
    title: "İptal ve İade Koşulları — Hümo",
    description: "İlk ödemeden sonra 14 gün koşulsuz tam iade, istediğiniz zaman iptal. İade nasıl istenir, ne zaman yansır.",
    index: true,
    priority: 0.3,
    changefreq: "yearly"
  },
  // Placeholder content until real posts / openings exist.
  {
    path: "/blog",
    title: "Blog — Hümo",
    description: "Hümo ekibinden müşteri mesajlaşması ve AI satış asistanları üzerine yazılar.",
    index: false
  },
  {
    path: "/careers",
    title: "Kariyer — Hümo",
    description: "Hümo ekibine katılın.",
    index: false
  }
];

/** Short or legacy paths that render a public page; their canonical is the long form. */
export const ROUTE_ALIASES: Record<string, string> = {
  "/pp": "/privacy-policy",
  "/privacy": "/privacy-policy",
  "/terms": "/terms-of-service"
};

const normalize = (pathname: string) => (pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname);

export function findPublicRoute(pathname: string): PublicRoute | undefined {
  const path = normalize(pathname);
  const target = ROUTE_ALIASES[path] ?? path;
  return PUBLIC_ROUTES.find((r) => r.path === target);
}

export const canonicalUrl = (route: PublicRoute) => (route.path === "/" ? `${SITE_URL}/` : `${SITE_URL}${route.path}`);
